//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  GetPinterestAccountAudienceStatsResponse,
  GetPinterestAccountAudienceSummaryStatsResponse,
  GetPinterestEngagementUserMetricsResponse,
} from '@bvengineering/pinterest-gateway-service-types';

import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import {
  IGetPinterestAccountAnalyticsRecentPinsResponse,
  IGetPinterestAccountAnalyticsTopPinsResponse,
  IMlsMaterializedPinWithMetrics,
  IPinterestAudienceDailyMetricsRequest,
  IPinterestAudienceStatsCsvRequest,
  IPinterestAudienceSummaryMetricsRequest,
  IPinterestEngagementMetricsRequest,
  IPinterestPerformanceOverTimeCsvRequest,
  IPinterestTopPinsCsvRequest,
  IPinterestTopPinsRequest,
} from '@apis/pinterest-analytics/pinterestAnalyticsApiModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { Serialized } from '@utils/types/Serialized';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const PINTEREST_ANALYTICS_ENDPOINT = '/api/v1/analytics';
const PINTEREST_ENGAGEMENT_ENDPOINT = '/api/v1/engagement';
const PINTEREST_AUDIENCE_ENDPOINT = '/api/v1/audience';

const pinterestGatewayAnalyticsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'pinterest_dashboard_gateway',
    `${PINTEREST_ANALYTICS_ENDPOINT}${endpoint}`,
    init,
  );
};

const pinterestGatewayEngagementFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'pinterest_dashboard_gateway',
    `${PINTEREST_ENGAGEMENT_ENDPOINT}${endpoint}`,
    init,
  );
};

const pinterestGatewayAudienceFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'pinterest_dashboard_gateway',
    `${PINTEREST_AUDIENCE_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets the client's engagement metrics
 *
 * GET /api/v1/engagement/metrics
 */
export const getEngagementMetrics = async (
  req: IPinterestEngagementMetricsRequest,
): Promise<GetPinterestEngagementUserMetricsResponse> => {
  const params = makeParams(req);
  const res = await pinterestGatewayEngagementFetch(`/metrics?${params}`);

  return res.json();
};

/*
 * Gets the client's audience daily metrics
 *
 * GET /api/v1/audience/stats
 */
export const getAudienceDailyMetrics = async (
  req: IPinterestAudienceDailyMetricsRequest,
): Promise<GetPinterestAccountAudienceStatsResponse> => {
  const params = makeParams(req);
  const res = await pinterestGatewayAudienceFetch(`/stats?${params}`);

  return res.json();
};

/*
 * Gets the client's audience summary metrics
 *
 * GET /api/v1/audience/summary-stats
 */
export const getAudienceSummaryMetrics = async (
  req: IPinterestAudienceSummaryMetricsRequest,
  init?: RequestInit,
): Promise<GetPinterestAccountAudienceSummaryStatsResponse> => {
  const params = makeParams(req);
  const res = await pinterestGatewayAudienceFetch(
    `/summary-stats?${params}`,
    init,
  );

  return res.json();
};

/**
 * Gets the client's recent pins with metrics
 *
 * GET /api/v1/analytics/recent-pins
 */
export const getRecentPins = async (): Promise<
  IMlsMaterializedPinWithMetrics[]
> => {
  const res = await pinterestGatewayAnalyticsFetch(`/recent-pins`);

  return (
    await res.json<IGetPinterestAccountAnalyticsRecentPinsResponse>(
      deserializePinsWithMetricsResponse,
    )
  ).data.pins;
};

/**
 * Gets the client's top pins with metrics
 *
 * GET /api/v1/analytics/top-pins
 */
export const getTopPins = async (
  req: IPinterestTopPinsRequest,
): Promise<IMlsMaterializedPinWithMetrics[]> => {
  const params = makeParams(req);
  const res = await pinterestGatewayAnalyticsFetch(`/top-pins?${params}`);

  return (
    await res.json<IGetPinterestAccountAnalyticsTopPinsResponse>(
      deserializePinsWithMetricsResponse,
    )
  ).data.pins;
};

export const deserializePinsWithMetricsResponse = (
  response: Serialized<
    | IGetPinterestAccountAnalyticsRecentPinsResponse
    | IGetPinterestAccountAnalyticsTopPinsResponse
  >,
):
  | IGetPinterestAccountAnalyticsRecentPinsResponse
  | IGetPinterestAccountAnalyticsTopPinsResponse => {
  return {
    data: {
      pins: deserializePinsWithMetrics(response.data.pins),
    },
  };
};

export const deserializePinsWithMetrics = (
  pins: Serialized<IMlsMaterializedPinWithMetrics[]>,
): IMlsMaterializedPinWithMetrics[] => {
  return pins.map((pinWithMetrics) => ({
    ...pinWithMetrics,
    mediaItem:
      pinWithMetrics.mediaItem &&
      deserializeMediaItem(pinWithMetrics.mediaItem),
  }));
};

export async function downloadAudienceCSV(
  req: IPinterestAudienceStatsCsvRequest,
): Promise<string> {
  const params = makeParams(req);
  const res = await pinterestGatewayAudienceFetch(`/stats.csv?${params}`);
  return await res.text();
}

export async function downloadPerformanceOverTimeCSV(
  req: IPinterestPerformanceOverTimeCsvRequest,
): Promise<string> {
  const params = makeParams(req);
  const res = await pinterestGatewayEngagementFetch(`/metrics.csv?${params}`);
  return await res.text();
}

export async function downloadTopPinsCSV(
  req: IPinterestTopPinsCsvRequest,
): Promise<string> {
  const params = makeParams(req);
  const res = await pinterestGatewayAnalyticsFetch(`/top-pins.csv?${params}`);
  return await res.text();
}
